<template>
  <div class="playoff-match"
    :class="{ 'match-finished': game.is_finished, 'match-upcoming': !game.is_finished, 'is-centered': centered }">
    <div class="match-header">
      <span class="match-date">{{ game.datetime }}</span>
      <span v-if="game.venue" class="match-venue">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 0C4.1 0 1 3.1 1 7c0 5.4 7 9 7 9s7-3.6 7-9c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S6.6 4.5 8 4.5s2.5 1.1 2.5 2.5S9.4 9.5 8 9.5z"
            fill="currentColor" />
        </svg>
        {{ game.venue }}
      </span>
    </div>

    <div class="match-teams">
      <component
        :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
        :href="teamPublicPath(game.home_team) || undefined"
        class="match-team"
        :class="{ 'match-team--link': !!teamPublicPath(game.home_team) }"
        @click.stop
      >
        <div class="team-logo" v-if="game.home_team.logo_path">
          <img :src="game.home_team.logo_path" :alt="game.home_team.name" />
        </div>
        <div class="team-logo placeholder" v-else>
          {{ game.home_team.name?.charAt(0) }}
        </div>
        <span class="team-name">{{ game.home_team.name }}</span>
      </component>
      <span v-if="game.is_finished" class="team-score">{{ game.home_score }}</span>

      <div class="match-separator">
        <span v-if="!game.is_finished">VS</span>
        <span v-else>:</span>
      </div>

      <span v-if="game.is_finished" class="team-score">{{ game.away_score }}</span>
      <component
        :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
        :href="teamPublicPath(game.away_team) || undefined"
        class="match-team"
        :class="{ 'match-team--link': !!teamPublicPath(game.away_team) }"
        @click.stop
      >
        <div class="team-logo" v-if="game.away_team.logo_path">
          <img :src="game.away_team.logo_path" :alt="game.away_team.name" />
        </div>
        <div class="team-logo placeholder" v-else>
          {{ game.away_team.name?.charAt(0) }}
        </div>
        <span class="team-name">{{ game.away_team.name }}</span>
      </component>
    </div>

    <div v-if="game.is_finished && game.sets_score && Array.isArray(game.sets_score) && game.sets_score.length > 0"
      class="match-sets">
      <span class="sets-label">Сеты:</span>
      <span class="sets-value">{{ formatSetsScore(game.sets_score) }}</span>
    </div>

    <div class="match-actions" v-if="game.is_finished">
      <a v-if="game.match_video_url" :href="game.match_video_url" target="_blank" rel="noopener noreferrer"
        class="match-video-link">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4 8l-5-3v6l5-3z" fill="currentColor" />
        </svg>
        Видео
      </a>
      <a v-if="game.has_stats" :href="gamePublicPath(game)" class="match-stats-link">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
        </svg>
        Статистика матча
      </a>
      <span v-else class="match-stats-unavailable" aria-disabled="true">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z" fill="currentColor" />
        </svg>
        Статистика недоступна
      </span>
    </div>

    <div v-else class="match-upcoming">
      <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
        <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm0 14A6 6 0 1 1 8 2a6 6 0 0 1 0 12zm.5-9H7v4l3.5 2.1.5-.8-3-1.8V5z"
          fill="currentColor" />
      </svg>
      <span>Матч запланирован</span>
    </div>
  </div>
</template>

<script setup>
import { formatSetsScore } from '@/utils/matchHelpers';
import { gamePublicPath } from '@/utils/gameUrl';
import { teamPublicPath } from '@/utils/teamUrl';
const props = defineProps({
  game: {
    type: Object,
    required: true
  },
  centered: {
    type: Boolean,
    default: false
  }
});
</script>

<style lang="scss" scoped>
.playoff-match {
  /* base wrapper */
  background: #fff;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border-color: #e6eef7;
  }

  &.match-finished {
    background: #f8fafc;
  }

  /* header */
  .match-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
    color: #475569;
  }

  .match-date {
    font-weight: 600;
    color: #475569;
  }

  .match-venue {
    display: flex;
    align-items: center;
    gap: 0.375rem;

    svg {
      opacity: 0.6;
    }
  }

  /* teams block */
  .match-teams {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem;
    background: #f8fafc;
    border-radius: 8px;
  }

  .match-team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    flex: 1;

    &--link {
      color: inherit;
      text-decoration: none;

      &:hover .team-name {
        text-decoration: underline;
      }
    }
  }

  /* team visual (logo + texts) */
  .team-logo {
    width: 48px;
    height: 48px;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    border-radius: 6px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .team-logo.placeholder {
    font-size: 1.125rem;
    color: #64748b;
    background: #f1f5f9;
  }

  .team-name {
    flex: 1;
    font-size: 0.95rem;
    font-weight: 600;
    color: #1e293b;
    text-align: left;
  }

  .team-score {
    min-width: 32px;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }

  /* separator / vs */
  .match-separator {
    font-size: 0.95rem;
    font-weight: 600;
    color: #94a3b8;
    padding: 0 0.5rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .match-vs {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 60px;
  }

  /* sets */
  .match-sets {
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f1f5f9;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #64748b;
  }

  .sets-label {
    font-weight: 600;
  }

  .sets-value {
    color: #475569;
  }

  /* actions / links */
  .match-actions {
    margin-top: 0.5rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .match-video-link,
  .match-stats-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.875rem;

    svg {
      color: currentColor;
    }

    &:hover {
      color: #2563eb;
    }
  }

  .match-stats-unavailable {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #94a3b8;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: default;
    user-select: none;

    svg {
      color: currentColor;
      opacity: 0.85;
    }
  }

  /* upcoming */
  .match-upcoming {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #eff6ff;
    border-radius: 6px;
    color: #64748b;
    font-weight: 500;
    font-size: 0.9rem;
  }

  /* centered large variant (for single-match pages) */
  &.is-centered {
    width: 50%;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
